var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { generateUUID } from '@utils';
/**
 * Upload one file to the chunked endpoint (`uploadlargefile`), used when a file
 * is too big for a single request. A single-shot POST is capped by Kestrel's
 * default request body limit of roughly 28.6MB, which nothing in the platform
 * raises, so beyond that this is the only path that works.
 *
 * The server speaks the resumable.js protocol, and both endpoints converge on
 * the same handler once the last chunk lands, so virus scanning, the extension
 * whitelist and final storage are identical to a small upload.
 *
 * resumable.js is pulled in on demand: it is only needed once a large file
 * actually appears, and must not sit on the boot path.
 */
export function uploadChunked(context, file, options) {
    return __awaiter(this, void 0, void 0, function* () {
        const { default: Resumable } = yield import(/* webpackChunkName: "chunked-upload" */ 'resumablejs');
        const base = context.lucyUrl.endsWith('/') ? context.lucyUrl : context.lucyUrl + '/';
        const chunkSizeMB = context.uploadChunkSizeMB || 1;
        // One token for the whole file. Left blank, the server mints a fresh GUID
        // per request, which scatters the chunks across separate folders that are
        // then never assembled.
        const uploadToken = generateUUID();
        const resumable = new Resumable({
            target: `${base}uploadlargefile/${options.path}`,
            chunkSize: chunkSizeMB * 1024 * 1024,
            simultaneousUploads: 1,
            fileParameterName: 'file',
            headers: { Authorization: context.apiKey },
            query: Object.assign(Object.assign({}, options.params), { uploadToken, 
                // The server reads `filename` for the name the file is finally
                // stored under and fails without it. `resumableFilename`, which the
                // library sends by itself, only names the temporary chunks.
                filename: options.fileName }),
        });
        // Without the File APIs, addFile is a no-op and the promise below would
        // never settle. Fail loudly instead of hanging.
        if (!resumable.support)
            throw new Error('This browser cannot upload files this large.');
        return new Promise((resolve, reject) => {
            resumable.on('fileProgress', (f) => {
                var _a;
                (_a = options.onProgress) === null || _a === void 0 ? void 0 : _a.call(options, Math.round(f.progress(false) * 100));
            });
            resumable.on('fileSuccess', () => resolve());
            resumable.on('fileError', (_f, message) => reject(new Error(message || 'Upload failed')));
            // addFile queues the file asynchronously: the library only pushes it onto
            // its list inside a setTimeout, then fires filesAdded. Calling upload()
            // straight after addFile finds an empty queue, sends nothing, and fires
            // no event at all, so the promise would hang forever. Start the transfer
            // from the event that says the file is actually queued.
            resumable.on('filesAdded', (files) => {
                if (!files || files.length === 0) {
                    reject(new Error('The file could not be queued for upload.'));
                    return;
                }
                resumable.upload();
            });
            resumable.addFile(file);
        });
    });
}
